/**
 * Session Card component
 * Displays a single session summary in a card format
 */

import React, { useState } from 'react';
import Card from '@amzn/meridian/card';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { SessionListDTO, deleteSession } from '../../../store/sessionSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { getSessionIdentifier, getSessionDisplayTitle } from '../../../utils/sessionDisplay';
import { isSessionDeletable } from '../../../utils/sessionMergeState';
import { withCondition } from '../../../utils/withCondition';
import { METADATA_KEYS } from '../../../constants';
import { sessionToMergeBadge, SessionBadge } from './dataTransformations';
import SessionBadgeComponent from './SessionBadge';
import SessionMergeSelectionComponent, { SessionMergeSelection } from './SessionMergeSelection';

interface SessionCardProps {
  session: SessionListDTO;
  onClick: () => void;
  showCreator?: boolean;
  /** Present in merge mode: the card's merge checkbox. */
  mergeSelection?: SessionMergeSelection;
}

const SessionBadgeWithCondition = withCondition(SessionBadgeComponent);
const SessionMergeSelectionWithCondition = withCondition(SessionMergeSelectionComponent);

const NO_BADGE: SessionBadge = { label: '', background: '', color: '' };
const NO_MERGE_SELECTION: SessionMergeSelection = {
  label: '',
  checked: false,
  disabled: true,
  onChange: () => {},
};

const truncateStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const cardWrapper: React.CSSProperties = {
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

/** The furthest-along review badge: the audit state once one exists, else validated. A session
 *  mid-validation reads as "not reviewed yet" and gets no badge. */
const reviewBadgeFor = (session: SessionListDTO) => {
  if (session.audit_state === 'audited') return { label: 'Audited', background: '#e3f2fd', color: '#0d47a1' };
  if (session.audit_state === 'auditing') return { label: 'Audit in progress', background: '#fff8e1', color: '#8d6e00' };
  if (session.validation_state === 'validated') return { label: 'Validated', background: '#e8f5e9', color: '#1b5e20' };
  return undefined;
};

const SessionCard: React.FC<SessionCardProps> = ({ session, onClick, showCreator = false, mergeSelection }) => {
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const canDelete = useAppSelector((state) => state.user.isEngineer);
  const reviewBadge = reviewBadgeFor(session);
  const mergeBadge = sessionToMergeBadge(session);
  const MergeBadge = SessionBadgeWithCondition(mergeBadge !== null);
  const MergeSelection = SessionMergeSelectionWithCondition(mergeSelection !== undefined);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      `Delete this session?\n\nThis will permanently remove the session and all associated data (audio files, transcripts, analysis results).\n\nSession: ${getSessionIdentifier(session.metadata, session.session_id).value}`
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteSession(session.session_id)).unwrap();
      dispatch(showSuccess('Session deleted successfully'));
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to delete session'));
    } finally {
      setIsDeleting(false);
    }
  };

  const title = getSessionDisplayTitle(session.metadata, session.session_id);
  const snapshot = session.metadata?.[METADATA_KEYS.SESSION_SNAPSHOT];
  // Recording sessions are busy and merge-locked ones (archived, or mid-merge) can't change, so
  // neither is deletable.
  const isDeletable = isSessionDeletable(session);
  // When the title is a user-set name, the Case ID is no longer in the title —
  // surface it as a secondary line so it stays visible.
  const sessionName = session.metadata?.[METADATA_KEYS.SESSION_NAME];
  const caseId = session.metadata?.[METADATA_KEYS.CASE_ID];
  const showCaseId = !!(sessionName && caseId);

  // In merge mode a card that can't be picked ignores clicks, so it doesn't look clickable either.
  const isClickable = !mergeSelection?.disabled;

  return (
    <div
      onClick={onClick}
      style={isClickable ? cardWrapper : { ...cardWrapper, cursor: 'default' }}
      onMouseEnter={(e) => {
        if (!isClickable) return;
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <Card spacingInset="400">
        <MergeSelection {...(mergeSelection ?? NO_MERGE_SELECTION)} />

        {/* Row 1: Date (+ Creator) + Delete button */}
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Text type="b200" color="secondary">
            {formatDate(session.created_at)}
            {showCreator && (
              <span style={{ marginLeft: '8px', fontWeight: 500, color: '#0073bb' }}>
                @{session.created_by}
              </span>
            )}
            {session.group_id && (
              <span style={{ marginLeft: '8px' }}>
                {session.group_id}
              </span>
            )}
            {showCaseId && (
              <span style={{ marginLeft: '8px' }}>
                Case ID: {caseId}
              </span>
            )}
          </Text>
          {canDelete && isDeletable && (
            isDeleting ? (
              <Loader size="small" />
            ) : (
              <div onClick={(e) => e.stopPropagation()}>
                <Button type="icon" size="small" onClick={handleDelete} aria-label="Delete session">
                  <Icon tokens={closeSmallTokens} />
                </Button>
              </div>
            )
          )}
        </Row>

        {/* Row 2: Title (name, Case ID, or Session ID) + review-state badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{ ...truncateStyle, minWidth: 0 }}>
            <Text type="b300" tag="span">
              <strong>{title}</strong>
            </Text>
          </div>
          {reviewBadge && (
            <span
              style={{
                flexShrink: 0,
                fontSize: '11px',
                backgroundColor: reviewBadge.background,
                color: reviewBadge.color,
                padding: '2px 8px',
                borderRadius: '10px',
                fontWeight: 600,
              }}
            >
              {reviewBadge.label}
            </span>
          )}
          <MergeBadge {...(mergeBadge ?? NO_BADGE)} />
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid #e0e0e0', marginBottom: '8px' }} />

        {/* Snapshot */}
        <div style={truncateStyle}>
          <Text type="b200" color="secondary" tag="span">
            <em>{snapshot ? `"${snapshot}..."` : '(No transcript yet)'}</em>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default SessionCard;
